/**
 * One game, as a card in a grid.
 *
 * Built to `docs/design-screenshots/revelry-game-list-page.png`: a picture,
 * the name, then the three facts somebody choosing a game actually asks for -
 * how many people, how hard, how long - each on its own line behind its own
 * mark. Everything else is on the detail page.
 *
 * ## Stacked, where it used to be side by side
 *
 * The art sat BESIDE the text for a while, and the reason was real: stacked,
 * every card cost the height of the picture plus three lines, and a list of
 * games was mostly scrolling. What changed is that the facts are three short
 * rows rather than a blurb and a wrapping run of chips, so the text column is
 * no taller than the picture beside it would have been - and a picture the
 * full width of the card is the thing that makes one game tell itself apart
 * from another at a glance.
 *
 * ## No price
 *
 * Decided: the library says what you can play, the store says what things
 * cost. A price under every card is the one fact that stops mattering the
 * moment you own the game.
 */

import { Link } from 'react-router-dom';
import type { CatalogGame } from '../net/session.js';
import { Icon } from '../render/Icon.js';
import { playerCount } from '../render/players.js';
import { gameArt, heroArt } from '../render/game-art.js';

export function GameCard({
  game,
  showOwned,
  compact,
  starred,
  onStar,
  onPick,
  disabled,
  note,
}: {
  game: CatalogGame;
  /**
   * A teaser rather than a choice (D6).
   *
   * The landing strip shows the art and the name and nothing else, which is
   * what both mockups draw. It is the same card underneath — same link, same
   * art, same name — with the detail dropped, rather than a second component
   * that would be a second place to change when a game gains a field.
   */
  compact?: boolean | undefined;
  /**
   * Mark this as already yours (K7).
   *
   * Off by default, because in the library every card in the first section is
   * owned and a chip on all of them says nothing. In the store the grid mixes
   * both, and "do I already have this" is the question somebody is there to
   * answer. It sits ON the picture now rather than among the facts, so the
   * three rows are the same three rows on every card everywhere.
   */
  showOwned?: boolean | undefined;
  /**
   * Starred, and what to do when the heart is pressed.
   *
   * Passed in rather than read here, so one page does one read and every card
   * on it shares the answer - a hook per card would be a request per card.
   * Absent on the landing strip, where nobody is signed in yet.
   */
  starred?: boolean | undefined;
  onStar?: (() => void) | undefined;
  /**
   * Press to CHOOSE this game rather than to read about it.
   *
   * The room's game picker shows the same shelves of the same cards as the
   * library does, and the only difference is what a press means: there, open
   * the page; here, change what the room is playing. A second card component
   * for that would be a second place to change every time a game gains a
   * field - the mistake `SettingRow` exists to have stopped making.
   *
   * The star is not drawn alongside this. A button inside a button is invalid
   * HTML, and favouriting is not a thing anybody is doing mid-room anyway.
   */
  onPick?: (() => void) | undefined;
  /** Dims and refuses the press - the game already being played. */
  disabled?: boolean | undefined;
  /**
   * A line under the name, for something true of this game HERE.
   *
   * "Playing now", or that the room has more people than it seats. Said on
   * the card rather than discovered at Start, because switching moves nobody
   * and a game that cannot seat the room needs somebody sent to watching
   * first.
   */
  note?: string | undefined;
}) {
  const art = gameArt(game.id, game.family);

  /*
   * Its own picture, where one exists.
   *
   * `GAME_ART` is written by the asset build from whatever files are there, so
   * this asks a list rather than asking the network and handling a 404 on
   * twelve cards of a page where nothing is wrong. A game with no picture
   * draws its mark at hero size in the same box, which is whole rather than
   * empty and improves one file at a time as art arrives.
   */
  const hero = heroArt(game.id);

  const classes = [
    'game-card',
    compact ? 'compact' : '',
    onPick && disabled ? 'chosen' : '',
  ]
    .filter(Boolean)
    .join(' ');

  /* Everything below is a span, so it is as legal inside a button as inside a
     link - the one exception is the star, which is a button and is therefore
     not offered when this one is. */
  const inside = (
    <>
      <span className={`game-card-art tone-${art.tone}`}>
        {hero === null ? (
          /* Decoration; the name below is the content, and `Icon` is already
             `aria-hidden` so a screen reader hears the game once. */
          <Icon name={art.icon} />
        ) : (
          <img src={hero} alt="" loading="lazy" />
        )}
        {showOwned && game.owned ? <span className="game-card-owned">Yours</span> : null}
      </span>

      {/*
        The heart, over the picture but deliberately not inside it.

        `.game-card-art` carries the card's `tone-` class, and the tone rules
        repaint any `currentColor` mark inside it with that tone's gradient -
        which is what gives a game with no art its gold or red or dark plate.
        With the heart in there it was picking up whichever tone the card
        happened to be, so the same control was gold on one game, red on the
        next and near-white on a third, and none of it meant anything. Out
        here it answers to its own two colours and nothing else.

        A button inside a link, which needs `preventDefault` or starring a
        game also opens it - the click reaches the anchor on its way up
        whatever the button does.
      */}
      {onStar === undefined || onPick !== undefined ? null : (
        <button
          type="button"
          className={starred ? 'game-card-star on' : 'game-card-star'}
          aria-pressed={starred === true}
          aria-label={starred ? `Unstar ${game.name}` : `Star ${game.name}`}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            onStar();
          }}
        >
          {/*
            The shape carries the state, not just the colour.

            It was a filled heart either way, so an unstarred game already
            showed a solid heart and the button read as "starred" before
            anybody touched it. Empty is an offer; full is an answer.
          */}
          <Icon name={starred ? 'heart' : 'heart-outline'} />
        </button>
      )}

      <span className="game-card-text">
        <span className="game-card-name">{game.name}</span>

        {compact ? null : (
          <span className="game-card-facts">
            <span className="game-card-fact">
              <Icon name="people" />
              {playerCount(game.minPlayers, game.maxPlayers)}
            </span>

            {/*
              How hard to LEARN, never how hard to play well (E7). Absent on a
              server that predates the field, rather than drawn as a blank row
              that would leave one card a line shorter than its neighbours.
            */}
            {game.difficulty ? (
              <span className={`game-card-fact difficulty ${game.difficulty.toLowerCase()}`}>
                <Icon name="difficulty" />
                {game.difficulty}
              </span>
            ) : null}

            <span className="game-card-fact">
              <Icon name="clock" />
              {game.playTimeMinutes} min
            </span>
          </span>
        )}

        {note === undefined ? null : <span className="game-card-note">{note}</span>}
      </span>
    </>
  );

  if (onPick) {
    return (
      <button type="button" className={classes} disabled={disabled} onClick={onPick}>
        {inside}
      </button>
    );
  }

  return (
    <Link className={classes} to={`/game/${game.id}`}>
      {inside}
    </Link>
  );
}
